import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@/icons';
import { Modal } from './Modal';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';

export interface CaseLoadErrorModalProps {
    isOpen: boolean;
    message: string | null;
    onReturnHome: () => void;
}

/**
 * Shown when SimpleCaseContext.loadCase (Provider.tsx) fails -- e.g. the case was deleted/not
 * found in blob storage, or a network/server error during load. Without this, the wizard page
 * underneath is stuck forever: `loading` is false but `formData`/`currentCase` never populate,
 * and most wizard pages' `if (loading || !formData) return <spinner>` guard runs before their
 * own (per-page, inconsistent) `if (error)` branch ever gets a chance to render. This modal is
 * the single, centralized place that surfaces the failure and gets the user back to a working
 * screen, regardless of which wizard page they landed on.
 */
export const CaseLoadErrorModal: React.FC<CaseLoadErrorModalProps> = ({ isOpen, message, onReturnHome }) => {
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);

    if (!isOpen) {
        return null;
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onReturnHome}
            title="Unable to Load Case"
            headerBackgroundColor={theme.colors.status.error}
            showCloseButton={false}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: theme.spacing.md,
                    padding: theme.spacing.md,
                    backgroundColor: '#fef2f2',
                    borderRadius: theme.borderRadius.sm,
                    border: `1px solid ${theme.colors.status.error}`,
                }}>
                    <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        style={{ fontSize: theme.typography.fontSize.lg, color: '#991b1b', marginTop: theme.spacing.xs }}
                    />
                    <div style={{ flex: 1 }}>
                        <p style={{
                            ...themedStyles.text.body,
                            color: '#991b1b',
                            fontWeight: theme.typography.fontWeight.medium,
                            margin: `0 0 ${theme.spacing.xs} 0`,
                        }}>
                            This case could not be loaded
                        </p>
                        <p style={{
                            ...themedStyles.text.body,
                            color: '#991b1b',
                            fontSize: theme.typography.fontSize.sm,
                            margin: 0,
                        }}>
                            {message ?? 'An unexpected error occurred.'}
                        </p>
                    </div>
                </div>

                <button
                    type="button"
                    onClick={onReturnHome}
                    style={themedStyles.button.primary}
                >
                    Return to Homepage
                </button>
            </div>
        </Modal>
    );
};
